import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getOrder } from '../utils/api';

/* ── inject styles once ── */
const injectOnce = (() => {
  let done = false;
  return () => {
    if (done) return; done = true;
    const s = document.createElement('style');
    s.textContent = `
      @keyframes os-enter{0%{opacity:0;transform:translateY(16px)}100%{opacity:1;transform:translateY(0)}}
      @keyframes os-ring{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
      @keyframes os-breathe{0%,100%{box-shadow:0 0 20px rgba(46,232,197,.12)}50%{box-shadow:0 0 40px rgba(46,232,197,.28)}}
      @keyframes os-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
      @keyframes os-dot{0%,100%{opacity:1}50%{opacity:.2}}
      @keyframes os-glow{0%,100%{opacity:.4}50%{opacity:.8}}
      @keyframes os-slide{0%{opacity:0;transform:translateX(-14px)}100%{opacity:1;transform:translateX(0)}}
      .os-e{animation:os-enter .5s cubic-bezier(.22,1,.36,1) both}
      .os-d1{animation-delay:.06s}.os-d2{animation-delay:.12s}.os-d3{animation-delay:.18s}.os-d4{animation-delay:.24s}
      .os-page{max-width:700px;margin:0 auto;padding:1.5rem 1rem 4rem;position:relative;z-index:1}
      .os-glass{background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:20px;padding:1.5rem;backdrop-filter:blur(16px);position:relative;overflow:hidden}
      .os-glass-glow{position:absolute;top:-1px;left:15%;right:15%;height:2px;border-radius:1px;animation:os-glow 3s ease-in-out infinite}
      .os-row{display:flex;justify-content:space-between;align-items:center;padding:.6rem 0;border-bottom:1px solid rgba(255,255,255,.04)}
      .os-row:last-child{border-bottom:none}
      .os-label{font-size:.75rem;color:var(--text-secondary,#8a8a9a)}
      .os-val{font-size:.78rem;font-weight:600}
      .os-pill{display:inline-flex;align-items:center;gap:5px;padding:4px 12px;border-radius:999px;font-size:.65rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
      .os-btn{display:inline-flex;align-items:center;gap:8px;padding:.7rem 1.5rem;border-radius:12px;font-size:.82rem;font-weight:700;cursor:pointer;transition:all .25s;text-decoration:none;border:none;font-family:inherit}
      .os-btn-primary{background:var(--accent,#2ee8c5);color:#0a0a0f;box-shadow:0 4px 18px rgba(46,232,197,.2)}
      .os-btn-ghost{background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);color:var(--text-secondary,#8a8a9a)}
      .os-feed-item{display:flex;align-items:center;gap:12px;padding:.65rem .85rem;background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.05);border-radius:12px;animation:os-slide .4s cubic-bezier(.22,1,.36,1) both;transition:border-color .2s}
      .os-feed-item:hover{border-color:rgba(255,255,255,.1)}
      .os-icon-sm{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:.85rem;font-weight:700}
      .os-copy-wrap{display:flex;align-items:center;gap:8px}
      .os-copy-btn{width:28px;height:28px;border-radius:7px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .2s;position:relative;flex-shrink:0}
      .os-copy-btn:hover{background:rgba(46,232,197,.08);border-color:rgba(46,232,197,.2)}
      .os-copy-btn svg{transition:all .25s;position:absolute}
      .os-copy-btn .os-cp-icon{opacity:1;transform:scale(1)}
      .os-copy-btn .os-ck-icon{opacity:0;transform:scale(.5)}
      .os-copy-btn.copied{background:rgba(46,232,197,.1);border-color:rgba(46,232,197,.25)}
      .os-copy-btn.copied .os-cp-icon{opacity:0;transform:scale(.5)}
      .os-copy-btn.copied .os-ck-icon{opacity:1;transform:scale(1)}
      .os-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%) translateY(16px);padding:.5rem 1.15rem;border-radius:12px;font-size:.72rem;font-weight:600;background:rgba(46,232,197,.1);border:1px solid rgba(46,232,197,.2);color:var(--accent,#2ee8c5);display:flex;align-items:center;gap:8px;opacity:0;transition:all .35s cubic-bezier(.22,1,.36,1);pointer-events:none;backdrop-filter:blur(12px);z-index:50}
      .os-toast.show{opacity:1;transform:translateX(-50%) translateY(0)}
    `;
    document.head.appendChild(s);
  };
})();

/* ── status helpers ── */
const STATUS_MAP = {
  completed: { label: 'COMPLETED', color: '#2ee8c5', bg: 'rgba(46,232,197,.1)', border: 'rgba(46,232,197,.2)' },
  failed: { label: 'FAILED', color: '#f0485f', bg: 'rgba(240,72,95,.08)', border: 'rgba(240,72,95,.15)' },
  processing: { label: 'PROCESSING', color: '#f59e0b', bg: 'rgba(245,158,11,.1)', border: 'rgba(245,158,11,.2)' },
  confirmed: { label: 'CONFIRMED', color: '#2ee8c5', bg: 'rgba(46,232,197,.1)', border: 'rgba(46,232,197,.2)' },
  confirming: { label: 'CONFIRMING', color: '#3b82f6', bg: 'rgba(46,232,197,.1)', border: 'rgba(46,232,197,.2)' },
  waiting: { label: 'AWAITING PAYMENT', color: '#f59e0b', bg: 'rgba(245,158,11,.1)', border: 'rgba(245,158,11,.2)' },
  expired: { label: 'EXPIRED', color: '#f59e0b', bg: 'rgba(245,158,11,.1)', border: 'rgba(245,158,11,.2)' },
};

const getStatus = (order) => {
  if (order.boostStatus === 'completed') return STATUS_MAP.completed;
  if (order.boostStatus === 'failed') return STATUS_MAP.failed;
  if (order.boostStatus === 'processing') return STATUS_MAP.processing;
  if (order.status === 'expired') return STATUS_MAP.expired;
  return STATUS_MAP[order.status] || STATUS_MAP.waiting;
};

const fmtDate = ts => ts ? new Date(ts).toLocaleString() : '—';
const fmtTime = ts => ts ? new Date(ts).toLocaleTimeString() : '';

const OrderStatus = () => {
  injectOnce();
  const { orderId: urlOrderId } = useParams();
  const [invoiceId, setInvoiceId] = useState(urlOrderId || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('Order ID copied!');
  const [orderTokens, setOrderTokens] = useState([]);
  const [orderLicenseKeys, setOrderLicenseKeys] = useState([]);

  const copyToClipboard = (text, msg) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setToastMsg(msg || 'Copied!');
    setCopied(true); setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
    setTimeout(() => setCopied(false), 2500);
  };

  const copyOrderId = (id) => copyToClipboard(id, 'Order ID copied!');
  const copyToken = (token) => copyToClipboard(token, 'Token copied!');

  const handleSearch = useCallback(async (id) => {
    const target = id || invoiceId;
    if (!target?.trim()) return;
    setLoading(true); setError(null);
    try { setOrder(await getOrder(target.trim())); }
    catch (e) { setError(e.message || 'Order not found'); setOrder(null); }
    finally { setLoading(false); }
  }, [invoiceId]);

  useEffect(() => { if (urlOrderId) { setInvoiceId(urlOrderId); handleSearch(urlOrderId); } }, [urlOrderId]);

  // Fetch tokens if order is completed token product
  useEffect(() => {
    if (!order?.id || order.boostStatus !== 'completed') { setOrderTokens([]); setOrderLicenseKeys([]); return; }
    fetch(`/api/order/${order.id}/tokens`)
      .then(r => r.json())
      .then(d => setOrderTokens(d.tokens || []))
      .catch(() => setOrderTokens([]));
    fetch(`/api/order/${order.id}/license-keys`)
      .then(r => r.json())
      .then(d => setOrderLicenseKeys(d.keys || []))
      .catch(() => setOrderLicenseKeys([]));
  }, [order?.id, order?.boostStatus]);

  useEffect(() => {
    if (!order) return;
    const isActive = ['waiting', 'confirming'].includes(order.status) || order.boostStatus === 'processing';
    if (!isActive) return;
    const iv = setInterval(() => handleSearch(order.id), 5000);
    return () => clearInterval(iv);
  }, [order?.id, order?.status, order?.boostStatus, handleSearch]);

  const st = order ? getStatus(order) : null;
  const log = order?.boostLog;
  const isActive = order && (['waiting', 'confirming'].includes(order.status) || order.boostStatus === 'processing');
  const isDone = order?.boostStatus === 'completed';
  const isFailed = order?.boostStatus === 'failed';
  const isExpired = order?.status === 'expired';
  const isToken = order?.productId?.startsWith('token_') || orderTokens.length > 0;
  const isTool = order?.productId?.startsWith('tool_') || orderLicenseKeys.length > 0;

  const delivered = log ? log.successCount * 2 : 0;
  const requested = log ? log.requested : (order?.quantity || 0);
  const pct = requested > 0 ? Math.min(1, delivered / requested) : 0;

  const timeline = [];
  if (order) {
    timeline.push({ label: 'Order Created', time: fmtTime(order.createdAt), done: true });
    if (order.status !== 'waiting' && !isExpired) timeline.push({ label: 'Payment Confirmed', time: '', done: true });
    else if (order.status === 'waiting') timeline.push({ label: 'Awaiting Payment', time: '', active: true });
    if (order.boostStatus === 'processing') timeline.push({ label: isToken ? 'Delivering Tokens…' : 'Delivering Boosts…', time: '', active: true });
    else if (isDone) timeline.push({ label: isToken ? 'Tokens Delivered' : 'Boosts Delivered', time: '', done: true });
    else if (isFailed) timeline.push({ label: 'Delivery Failed', time: '', failed: true });
    if (isExpired) timeline.push({ label: 'Order Expired', time: '', failed: true });
  }

  /* ── Search box ── */
  const searchBox = (
    <div className="os-glass os-e" style={{ marginBottom: '1.5rem' }}>
      <div className="os-glass-glow" style={{ background: 'linear-gradient(90deg,transparent,var(--accent,#2ee8c5),transparent)' }} />
      <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-.03em', marginBottom: 4 }}>Order Status</h1>
        <p style={{ fontSize: '.82rem', color: 'var(--text-secondary,#8a8a9a)' }}>Track your order in real-time</p>
      </div>
      <div style={{ display: 'flex', gap: '.5rem' }}>
        <input type="text" className="form-input" placeholder="Enter your invoice ID" value={invoiceId}
          onChange={e => setInvoiceId(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
          style={{ flex: 1, background: 'rgba(0,0,0,.25)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 12, padding: '.7rem 1rem', color: 'var(--text-primary,#e8e8ec)', fontSize: '.82rem', outline: 'none', fontFamily: 'inherit' }} />
        <button onClick={() => handleSearch()} disabled={loading || !invoiceId.trim()} className="os-btn os-btn-primary" style={{ padding: '.7rem 1.25rem' }}>
          {loading ? <span style={{ width: 18, height: 18, border: '2px solid rgba(0,0,0,.2)', borderTopColor: '#0a0a0f', borderRadius: '50%', animation: 'os-ring .7s linear infinite', display: 'inline-block' }} /> : 'Search'}
        </button>
      </div>
    </div>
  );

  /* Error */
  if (error) return (
    <div className="os-page">{searchBox}
      <div className="os-glass os-e os-d1" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(240,72,95,.08)', border: '2px solid rgba(240,72,95,.15)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f0485f" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        </div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 6 }}>Order Not Found</h2>
        <p style={{ fontSize: '.82rem', color: 'var(--text-secondary,#8a8a9a)', marginBottom: '1.25rem' }}>{error}</p>
        <Link to="/" className="os-btn os-btn-primary" style={{ textDecoration: 'none' }}>Create New Order</Link>
      </div>
    </div>
  );

  if (!order) return <div className="os-page">{searchBox}</div>;

  /* Expired */
  if (isExpired) return (
    <div className="os-page">{searchBox}
      <div className="os-glass os-e os-d1" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(245,158,11,.06)', border: '2px solid rgba(245,158,11,.15)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 6 }}>Payment Expired</h2>
        <p style={{ fontSize: '.82rem', color: 'var(--text-secondary,#8a8a9a)', marginBottom: '1.25rem' }}>The payment window for this order has closed.</p>
        <div className="os-glass" style={{ maxWidth: 340, margin: '0 auto 1.25rem', padding: '1rem' }}>
          <div className="os-row"><span className="os-label">Product</span><span className="os-val">{order.quantity}× {order.productName}</span></div>
          <div className="os-row"><span className="os-label">Amount</span><span className="os-val" style={{ color: '#2ee8c5' }}>${order.total?.toFixed(2)}</span></div>
        </div>
        <Link to="/" className="os-btn os-btn-primary" style={{ textDecoration: 'none' }}>Create New Order</Link>
      </div>
    </div>
  );

  /* ── Main view ── */
  const R = 50, C = 2 * Math.PI * R;

  return (
    <div className="os-page">{searchBox}

      {/* Status Hero */}
      <div className="os-glass os-e os-d1" style={{ textAlign: 'center', padding: '2rem 1.5rem', marginBottom: '1.25rem', borderColor: st.border }}>
        <div className="os-glass-glow" style={{ background: `linear-gradient(90deg,transparent,${st.color},transparent)` }} />
        <div style={{ width: 72, height: 72, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: st.bg, border: `2px solid ${st.border}`, marginBottom: '1rem', animation: isDone ? 'os-pulse 2s ease-in-out infinite' : isActive ? 'os-breathe 2.5s ease-in-out infinite' : 'none' }}>
          {isDone ? <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={st.color} strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            : <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={st.color} strokeWidth="2"><circle cx="12" cy="12" r="10"/>{isFailed ? <><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></> : <polyline points="12 6 12 12 16 14"/>}</svg>}
        </div>
        <div style={{ marginBottom: '.75rem' }}>
          <span className="os-pill" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.color, animation: isActive ? 'os-dot 1.5s ease-in-out infinite' : 'none' }} />
            {st.label}
          </span>
        </div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 6 }}>
          {isDone ? (isTool ? 'License Delivered! 🔧' : isToken ? 'Tokens Delivered! 🎟️' : 'Boosts Delivered! 🎉') : isFailed ? 'Delivery Failed' : isActive ? (isTool ? 'Generating License…' : isToken ? 'Delivering Tokens…' : 'Delivering Boosts…') : order.status === 'confirmed' ? 'Payment Confirmed' : 'Awaiting Payment'}
        </h2>
        <p style={{ fontSize: '.82rem', color: 'var(--text-secondary,#8a8a9a)', lineHeight: 1.5 }}>
          {isDone ? (isTool ? 'Your license key is ready below.' : isToken ? 'Your tokens are ready below.' : 'All boosts have been applied to your server.') : isFailed ? 'Delivery could not be completed. Please contact support.' : isActive ? 'This page updates automatically every 5 seconds.' : 'Waiting for payment to be detected.'}
        </p>
      </div>

      {/* Progress - hide for token orders */}
      {log && !isToken && (
        <div className="os-glass os-e os-d2" style={{ marginBottom: '1.25rem' }}>
          <div className="os-glass-glow" style={{ background: `linear-gradient(90deg,transparent,${st.color},transparent)` }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap', padding: '.5rem 0' }}>
            <div style={{ position: 'relative', width: 110, height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="110" height="110" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,.04)" strokeWidth="5.5"/>
                <circle cx="60" cy="60" r={R} fill="none" stroke={st.color} strokeWidth="5.5" strokeDasharray={C} strokeDashoffset={C*(1-pct)} strokeLinecap="round" style={{ transition: 'stroke-dashoffset .8s ease' }}/>
              </svg>
              <div style={{ position: 'absolute', textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: st.color }}>{Math.round(pct*100)}%</div>
                <div style={{ fontSize: '.58rem', fontWeight: 600, color: 'var(--text-muted,#55555f)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{delivered}/{requested}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .9rem', borderRadius: 12, background: 'rgba(46,232,197,.05)', border: '1px solid rgba(46,232,197,.1)', minWidth: 140 }}>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#2ee8c5' }}>{log.successCount}</span>
                <span style={{ fontSize: '.7rem', color: 'var(--text-secondary,#8a8a9a)' }}>Successful</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .9rem', borderRadius: 12, background: 'rgba(240,72,95,.04)', border: '1px solid rgba(240,72,95,.08)', minWidth: 140 }}>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#f0485f' }}>{log.failedCount + log.captchaCount}</span>
                <span style={{ fontSize: '.7rem', color: 'var(--text-secondary,#8a8a9a)' }}>Failed</span>
              </div>
              {isActive && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .9rem', borderRadius: 12, background: 'rgba(245,158,11,.04)', border: '1px solid rgba(245,158,11,.08)', minWidth: 140 }}>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#f59e0b' }}>{Math.max(0, Math.ceil(requested/2) - log.successCount - log.failedCount - log.captchaCount)}</span>
                <span style={{ fontSize: '.7rem', color: 'var(--text-secondary,#8a8a9a)' }}>Remaining</span>
              </div>}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="os-glass os-e os-d3" style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent,#2ee8c5)" strokeWidth="2"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          Order Timeline
        </div>
        <div style={{ position: 'relative', paddingLeft: 24 }}>
          <div style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 2, background: 'rgba(46,232,197,.1)', borderRadius: 1 }} />
          {timeline.map((item, idx) => (
            <div key={idx} style={{ position: 'relative', padding: '.55rem 0 .55rem .75rem' }}>
              <div style={{ position: 'absolute', left: -20, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%',
                background: item.done ? '#2ee8c5' : item.active ? '#f59e0b' : item.failed ? '#f0485f' : 'rgba(255,255,255,.1)',
                border: '2px solid #0a0a0f',
                boxShadow: item.done ? '0 0 8px rgba(46,232,197,.4)' : item.active ? '0 0 8px rgba(245,158,11,.4)' : item.failed ? '0 0 8px rgba(240,72,95,.4)' : 'none',
                animation: item.active ? 'os-dot 1.5s ease-in-out infinite' : 'none' }} />
              <div style={{ fontSize: '.78rem', fontWeight: 600, color: item.failed ? '#f0485f' : item.done ? 'var(--text-primary,#e8e8ec)' : 'var(--text-secondary,#8a8a9a)' }}>{item.label}</div>
              {item.time && <div style={{ fontSize: '.6rem', color: 'var(--text-muted,#55555f)', marginTop: 1 }}>{item.time}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Live Feed */}
      {log?.details?.length > 0 && (
        <div className="os-glass os-e os-d3" style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.85rem' }}>
            <div style={{ fontSize: '.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              {isActive && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2ee8c5', boxShadow: '0 0 8px #2ee8c5', animation: 'os-dot 2s ease-in-out infinite' }} />}
              {isActive ? 'Live Delivery Feed' : 'Delivery Log'}
            </div>
            {isActive && <span style={{ fontSize: '.62rem', color: 'var(--text-muted,#55555f)' }}>Auto-updating</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {[...log.details].reverse().slice(0, 20).map((item, idx) => (
              <div key={idx} className="os-feed-item" style={{ animationDelay: `${idx * 0.03}s` }}>
                <div className="os-icon-sm" style={{ background: item.status === 'success' ? 'rgba(46,232,197,.1)' : 'rgba(240,72,95,.08)', color: item.status === 'success' ? '#2ee8c5' : '#f0485f' }}>
                  {item.status === 'success' ? '✓' : '✗'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 600 }}>Boost {item.status === 'success' ? 'delivered' : 'failed'}</div>
                  <div style={{ fontSize: '.62rem', color: 'var(--text-muted,#55555f)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.message || 'Applied to server'}</div>
                </div>
                <div style={{ fontSize: '.58rem', color: 'var(--text-muted,#55555f)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{item.time ? fmtTime(item.time) : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Details */}
      <div className="os-glass os-e os-d4" style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent,#2ee8c5)" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
          Order Details
        </div>
        <div className="os-row"><span className="os-label">Order ID</span>
          <div className="os-copy-wrap">
            <code style={{ fontSize: '.68rem', background: 'rgba(255,255,255,.04)', padding: '3px 8px', borderRadius: 5, fontFamily: 'monospace' }}>{order.id}</code>
            <button className={`os-copy-btn${copied ? ' copied' : ''}`} onClick={() => copyOrderId(order.id)} title="Copy order ID">
              <svg className="os-cp-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary,#8a8a9a)" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              <svg className="os-ck-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent,#2ee8c5)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </button>
          </div>
        </div>
        <div className="os-row"><span className="os-label">Product</span><span className="os-val">{order.quantity}× {order.productName}</span></div>
        <div className="os-row"><span className="os-label">Payment</span><span className="os-val">{order.paymentMethod === 'stripe' ? 'Card (Stripe)' : order.paymentMethod === 'paypal' ? 'PayPal' : order.paidWithBalance ? 'Balance' : order.cryptoType?.replace('_', ' ') || 'Crypto'}</span></div>
        <div className="os-row"><span className="os-label">Total</span><span className="os-val" style={{ color: '#2ee8c5', fontWeight: 800 }}>${order.total?.toFixed(2)}</span></div>
        <div className="os-row"><span className="os-label">Created</span><span className="os-val">{fmtDate(order.createdAt)}</span></div>
        {['waiting', 'confirming'].includes(order.status) && <div className="os-row"><span className="os-label">Expires</span><span className="os-val">{fmtDate(order.expiresAt)}</span></div>}
      </div>

      {/* License key delivery display */}
      {orderLicenseKeys.length > 0 && isDone && (
        <div className="os-glass os-e os-d3" style={{ marginTop: '1rem', borderColor: 'rgba(46,232,197,.15)' }}>
          <div className="os-glass-glow" style={{ background: 'linear-gradient(90deg,transparent,var(--accent, #2ee8c5),transparent)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.1rem' }}>🔧</span>
            <span style={{ fontSize: '.85rem', fontWeight: 700 }}>License Key{orderLicenseKeys.length > 1 ? 's' : ''}</span>
            <span style={{ fontSize: '.65rem', color: '#8a8a9a' }}>— {orderLicenseKeys.length} key{orderLicenseKeys.length > 1 ? 's' : ''}</span>
            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
              <button onClick={() => { navigator.clipboard.writeText(orderLicenseKeys.join('\n')); setToastMsg('All keys copied!'); setShowToast(true); setTimeout(() => setShowToast(false), 2000); }} style={{ background: 'rgba(46,232,197,.08)', border: '1px solid rgba(46,232,197,.18)', borderRadius: 8, padding: '5px 12px', color: 'var(--accent, #2ee8c5)', fontSize: '.68rem', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                📋 Copy All
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {orderLicenseKeys.map((key, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(46,232,197,.04)', border: '1px solid rgba(46,232,197,.1)', borderRadius: 10 }}>
                <span style={{ background: 'rgba(46,232,197,.12)', borderRadius: 6, width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.6rem', color: 'var(--accent, #2ee8c5)', flexShrink: 0 }}>{i+1}</span>
                <code style={{ fontSize: '.72rem', fontFamily: "'SF Mono',Monaco,Consolas,monospace", color: 'var(--accent, #2ee8c5)', wordBreak: 'break-all', flex: 1, lineHeight: 1.4, letterSpacing: '.5px' }}>{key}</code>
                <button onClick={() => { navigator.clipboard.writeText(key); setToastMsg('Key copied!'); setShowToast(true); setTimeout(() => setShowToast(false), 2000); }} style={{ background: 'rgba(46,232,197,.08)', border: '1px solid rgba(46,232,197,.12)', borderRadius: 6, padding: '4px 8px', color: 'var(--accent, #2ee8c5)', fontSize: '.62rem', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>📋</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(46,232,197,.03)', border: '1px solid rgba(46,232,197,.08)', borderRadius: 8, fontSize: '.68rem', color: '#8a8a9a', lineHeight: 1.6 }}>
            Go to your <a href="/dashboard?tab=tools" style={{ color: 'var(--accent, #2ee8c5)', textDecoration: 'none', fontWeight: 600 }}>Dashboard → My Tools</a> to manage your license, download, and reset HWID.
          </div>
        </div>
      )}

      {/* Token delivery display */}
      {orderTokens.length > 0 && isDone && (
        <div className="os-glass os-e os-d3" style={{ marginTop: '1rem', borderColor: 'rgba(139,92,246,.15)' }}>
          <div className="os-glass-glow" style={{ background: 'linear-gradient(90deg,transparent,#a78bfa,transparent)' }} />
          
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.1rem' }}>🎟️</span>
            <span style={{ fontSize: '.85rem', fontWeight: 700 }}>Delivered Items</span>
            <span style={{ fontSize: '.65rem', color: '#8a8a9a' }}>— {orderTokens.length} token{orderTokens.length > 1 ? 's' : ''}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button onClick={() => { navigator.clipboard.writeText(orderTokens.join('\n')); setToastMsg('All tokens copied!'); setShowToast(true); setTimeout(() => setShowToast(false), 2000); }} style={{ background: 'rgba(0,229,208,.08)', border: '1px solid rgba(0,229,208,.18)', borderRadius: 8, padding: '5px 12px', color: 'var(--accent,#2ee8c5)', fontSize: '.68rem', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                Copy All
              </button>
              <button onClick={() => { const b = new Blob([orderTokens.join('\n')], { type: 'text/plain' }); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `tokens-${order.id.slice(0,8)}.txt`; a.click(); setToastMsg('File downloaded!'); setShowToast(true); setTimeout(() => setShowToast(false), 2000); }} style={{ background: 'rgba(139,92,246,.08)', border: '1px solid rgba(139,92,246,.18)', borderRadius: 8, padding: '5px 12px', color: '#a78bfa', fontSize: '.68rem', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download .txt
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div style={{ background: 'rgba(139,92,246,.04)', border: '1px solid rgba(139,92,246,.1)', borderRadius: 10, padding: '12px 16px', marginBottom: '1rem' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: '#a78bfa', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>Instructions</div>
            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '.72rem', color: 'var(--text-secondary,#8a8a9a)', lineHeight: 1.8, listStyle: 'none' }}>
              <li style={{ position: 'relative', paddingLeft: 4 }}>• Format: <span style={{ color: '#e8e8ec', fontFamily: 'monospace', fontSize: '.68rem' }}>email:pass:token</span></li>
              <li style={{ position: 'relative', paddingLeft: 4 }}>• Use trusted tools &amp; proxies — <strong style={{ color: '#e8e8ec' }}>do not login manually</strong></li>
              <li style={{ position: 'relative', paddingLeft: 4 }}>• Tokens are never resold or reused</li>
              <li style={{ position: 'relative', paddingLeft: 4 }}>• We do not replace if Discord revokes tokens in ban waves</li>
            </ul>
          </div>

          {/* Token list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {orderTokens.map((token, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(139,92,246,.04)', border: '1px solid rgba(139,92,246,.08)', borderRadius: 10, transition: 'border-color .2s' }}>
                <span style={{ fontSize: '.62rem', color: '#6b6b78', minWidth: 22, fontWeight: 600 }}>#{i + 1}</span>
                <code style={{ fontSize: '.68rem', fontFamily: "'SF Mono',Monaco,Consolas,monospace", color: '#d4d4dc', wordBreak: 'break-all', flex: 1, lineHeight: 1.4 }}>{token}</code>
                <button 
                  onClick={() => copyToken(token)} 
                  style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(139,92,246,.12)', background: 'rgba(139,92,246,.06)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' }}
                  title="Copy token"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                </button>
              </div>
            ))}
          </div>

          {/* Footer warning */}
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.04)', borderRadius: 8, fontSize: '.66rem', color: '#5a5a66', textAlign: 'center' }}>
            🔒 Keep these tokens safe — they can only be delivered once
          </div>
        </div>
      )}

      {(isDone || isFailed) && (
        <div className="os-e os-d4" style={{ display: 'flex', gap: '.65rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/" className="os-btn os-btn-primary" style={{ textDecoration: 'none' }}>Return Home</Link>
          {isFailed && <a href="https://discord.gg/outsourced" target="_blank" rel="noopener noreferrer" className="os-btn os-btn-ghost" style={{ textDecoration: 'none' }}>Contact Support</a>}
        </div>
      )}

      {/* Copy toast */}
      <div className={`os-toast${showToast ? ' show' : ''}`}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent,#2ee8c5)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        {toastMsg}
      </div>
    </div>
  );
};

export default OrderStatus;
